import { useState, useEffect, useCallback } from 'react'
import { Toast } from '@/components/Toast'
import { ID, PaginationResult } from '@/types/common'

/**
 * 通用 CRUD API 接口定义
 */
export interface CrudApi<T, TForm, TId extends ID = ID> {
  getList: (params?: Record<string, unknown>) => Promise<PaginationResult<T>>
  getById?: (id: TId) => Promise<T | null>
  create: (data: TForm) => Promise<T | void>
  update: (id: TId, data: TForm) => Promise<T | void>
  delete: (ids: TId[]) => Promise<void>
}

/**
 * 生命周期钩子
 */
export interface CrudLifecycleHooks<T, TForm, TId extends ID> {
  beforeSave?: (payload: { data: TForm; editingItem: T | null }) => Promise<TForm> | TForm
  afterSave?: (payload: { data: TForm; editingItem: T | null }) => Promise<void> | void
  beforeDelete?: (payload: { id: TId }) => Promise<boolean> | boolean
  afterDelete?: (payload: { id: TId }) => Promise<void> | void
  beforeBatchDelete?: (payload: { ids: TId[] }) => Promise<boolean> | boolean
  afterBatchDelete?: (payload: { ids: TId[] }) => Promise<void> | void
}

/**
 * useCrud 配置选项
 */
export interface CrudDataSource<T> {
  items: T[]
  loading: boolean
  reload?: () => Promise<void> | void
}

export interface UseCrudConfig<
  T extends { id: TId },
  TForm,
  TId extends ID = ID
> {
  api: CrudApi<T, TForm, TId>
  entityName: string
  messages?: {
    loadError?: string
    createSuccess?: string
    createError?: string
    updateSuccess?: string
    updateError?: string
    deleteSuccess?: string
    deleteError?: string
    batchDeleteSuccess?: (count: number) => string
    batchDeleteError?: string
    batchDeleteWarning?: string
  }
  autoLoad?: boolean
  idSelector?: (item: T) => TId
  hooks?: CrudLifecycleHooks<T, TForm, TId>
  dataSource?: () => CrudDataSource<T>
  pagination?: {
    enabled?: boolean
    page?: number
    pageSize?: number
  }
}

/**
 * @example
 * useCrud({
 *   api: studentApi,
 *   entityName: '学生',
 *   idSelector: (student) => student.id, // 支持 number 类型 ID
 *   hooks: {
 *     beforeSave: async ({ data, editingItem }) => {
 *       // 在保存前对数据做转换
 *       return { ...data, name: data.name.trim() }
 *     },
 *   },
 *   dataSource: () => useStudentsQuery(), // 使用 React Query 数据源
 * })
 */

/**
 * useCrud 返回值
 */
export interface UseCrudReturn<
  T extends { id: TId },
  TForm,
  TId extends ID = ID
> {
  // 数据
  items: T[]
  loading: boolean
  opLoading: boolean
  isModalOpen: boolean
  editingItem: T | null
  selectedIds: TId[]
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
  
  // 方法
  setSelectedIds: (ids: TId[]) => void
  handleAdd: () => void
  handleEdit: (item: T) => void
  handleSave: (data: TForm) => Promise<void>
  handleDelete: (id: TId) => Promise<void>
  handleBatchDelete: () => Promise<void>
  handleCloseModal: () => void
  loadItems: (pagination?: { page?: number; pageSize?: number }) => Promise<void>
  handlePageChange: (page: number, pageSize?: number) => void
}

/**
 * 通用 CRUD Hook
 * 
 * @example
 * ```tsx
 * const {
 *   items,
 *   loading,
 *   handleAdd,
 *   handleEdit,
 *   handleSave,
 *   handleDelete,
 * } = useCrud({
 *   api: domainApi,
 *   entityName: '领域',
 * })
 * ```
 */
export function useCrud<
  T extends { id: TId },
  TForm,
  TId extends ID = ID
>(
  config: UseCrudConfig<T, TForm, TId>
): UseCrudReturn<T, TForm, TId> {
  const {
    api,
    entityName,
    messages = {},
    autoLoad = true,
    idSelector,
    hooks,
  } = config

  // 默认消息
  const defaultMessages = {
    loadError: `加载${entityName}列表失败`,
    createSuccess: `${entityName}创建成功`,
    createError: `创建${entityName}失败`,
    updateSuccess: `${entityName}更新成功`,
    updateError: `更新${entityName}失败`,
    deleteSuccess: `${entityName}删除成功`,
    deleteError: `删除${entityName}失败`,
    batchDeleteSuccess: (count: number) => `成功删除 ${count} 个${entityName}`,
    batchDeleteError: `批量删除${entityName}失败`,
    batchDeleteWarning: `请选择要删除的${entityName}`,
    ...messages,
  }

  // 状态管理
  const [internalItems, setInternalItems] = useState<T[]>([])
  const [internalLoading, setInternalLoading] = useState(false)
  const [opLoading, setOpLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<T | null>(null)
  const [selectedIds, setSelectedIds] = useState<TId[]>([])
  const [pagination, setPagination] = useState({
    page: config.pagination?.page ?? 1,
    pageSize: config.pagination?.pageSize ?? 10,
    total: 0,
    totalPages: 0,
  })
  const externalDataSource = config.dataSource?.()
  const externalItems = externalDataSource?.items
  const externalLoading = externalDataSource?.loading
  const externalReload = externalDataSource?.reload

  const extractId = useCallback(
    (item: T): TId => {
      return idSelector ? idSelector(item) : item.id
    },
    [idSelector]
  )

  // 加载列表
  const loadItems = useCallback(async (nextPagination?: { page?: number; pageSize?: number }) => {
    if (externalReload) {
      await externalReload()
      return
    }

    try {
      setInternalLoading(true)
      const resolvedPage = nextPagination?.page ?? pagination.page
      const resolvedPageSize = nextPagination?.pageSize ?? pagination.pageSize
      const params = config.pagination?.enabled
        ? {
            page: resolvedPage,
            pageSize: resolvedPageSize,
          }
        : undefined
      const data = await api.getList(params)
      setInternalItems(Array.isArray(data?.data) ? data.data : [])
      if (data) {
        setPagination((prev) => ({
          page: data.page ?? resolvedPage ?? prev.page,
          pageSize: data.pageSize ?? resolvedPageSize ?? prev.pageSize,
          total: data.total ?? prev.total,
          totalPages: data.totalPages ?? prev.totalPages,
        }))
      }
    } catch (error) {
      Toast.error(defaultMessages.loadError)
      console.error(`Load ${entityName} error:`, error)
    } finally {
      setInternalLoading(false)
    }
  }, [
    api,
    entityName,
    defaultMessages.loadError,
    externalReload,
    config.pagination?.enabled,
    pagination.page,
    pagination.pageSize,
  ])

  // 初始化加载
  useEffect(() => {
    if (autoLoad && !externalReload) {
      loadItems()
    }
  }, [autoLoad, loadItems, externalReload])

  // 新增
  const handleAdd = useCallback(() => {
    setEditingItem(null)
    setIsModalOpen(true)
  }, [])

  // 编辑
  const handleEdit = useCallback((item: T) => {
    setEditingItem(item)
    setIsModalOpen(true)
  }, [])

  // 保存（新增或编辑）
  const handleSave = useCallback(
    async (data: TForm) => {
      try {
        setOpLoading(true)
        const payload =
          (await hooks?.beforeSave?.({ data, editingItem })) ?? data

        if (editingItem) {
          // 编辑模式
          await api.update(extractId(editingItem), payload)
          Toast.success(defaultMessages.updateSuccess)
        } else {
          // 新增模式
          await api.create(payload)
          Toast.success(defaultMessages.createSuccess)
        }

        setIsModalOpen(false)
        setEditingItem(null)
        await loadItems() // 重新加载列表
        await hooks?.afterSave?.({ data: payload, editingItem })
      } catch (error: any) {
        // 优先使用接口返回的具体错误消息
        Toast.error(
          error.message || (editingItem
            ? defaultMessages.updateError
            : defaultMessages.createError)
        )
        console.error(`Save ${entityName} error:`, error)
      } finally {
        setOpLoading(false)
      }
    },
    [
      editingItem,
      api,
      entityName,
      defaultMessages.updateSuccess,
      defaultMessages.updateError,
      defaultMessages.createSuccess,
      defaultMessages.createError,
      loadItems,
    ]
  )

  // 通用删除函数（支持单个和批量）
  const performDelete = useCallback(
    async (ids: TId[]) => {
      if (ids.length === 0) return

      const isBatch = ids.length > 1

      // 调用对应的 before hook
      if (isBatch) {
        const shouldContinue = await hooks?.beforeBatchDelete?.({ ids })
        if (shouldContinue === false) return
      } else {
        const shouldContinue = await hooks?.beforeDelete?.({ id: ids[0] })
        if (shouldContinue === false) return
      }

      try {
        setOpLoading(true)
        await api.delete(ids)

        // 显示成功消息
        if (isBatch) {
          Toast.success(defaultMessages.batchDeleteSuccess(ids.length))
        } else {
          Toast.success(defaultMessages.deleteSuccess)
        }

        // 如果是批量删除，清空选中项
        if (isBatch) {
          setSelectedIds([])
        }

        await loadItems()

        // 调用对应的 after hook
        if (isBatch) {
          await hooks?.afterBatchDelete?.({ ids })
        } else {
          await hooks?.afterDelete?.({ id: ids[0] })
        }
      } catch (error: any) {
        // 优先使用接口返回的具体错误消息
        if (isBatch) {
          Toast.error(error.message || defaultMessages.batchDeleteError)
        } else {
          Toast.error(error.message || defaultMessages.deleteError)
        }
        console.error(`${isBatch ? 'Batch ' : ''}Delete ${entityName} error:`, error)
      } finally {
        setOpLoading(false)
      }
    },
    [
      api,
      entityName,
      defaultMessages.deleteSuccess,
      defaultMessages.deleteError,
      defaultMessages.batchDeleteSuccess,
      defaultMessages.batchDeleteError,
      loadItems,
      hooks,
    ]
  )

  // 删除单个
  const handleDelete = useCallback(
    async (id: TId) => {
      await performDelete([id])
    },
    [performDelete]
  )

  // 批量删除
  const handleBatchDelete = useCallback(async () => {
    if (selectedIds.length === 0) {
      Toast.warning(defaultMessages.batchDeleteWarning)
      return
    }
    await performDelete(selectedIds)
  }, [selectedIds, performDelete, defaultMessages.batchDeleteWarning])

  // 关闭模态框
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setEditingItem(null)
  }, [])

  const computedItems = externalItems ?? internalItems
  const computedLoading = externalLoading ?? internalLoading

  return {
    items: computedItems,
    loading: computedLoading,
    opLoading,
    isModalOpen,
    editingItem,
    selectedIds,
    pagination,
    setSelectedIds,
    handleAdd,
    handleEdit,
    handleSave,
    handleDelete,
    handleBatchDelete,
    handleCloseModal,
    loadItems,
    handlePageChange: (page: number, pageSize?: number) => {
      loadItems({ page, pageSize })
    },
  }
}
